import deepmerge from 'deepmerge';

//  		autocomplete(inputEl, listFromValue, defaultsList, options) -> {destroy, close}
// 			- inputEl
// 			- listFromValue(stringValue, callbackFn) {
// 				callbackFn([listItem, listItem, ....])
// 			}
// 			- options:
// 				- render (scope is autocomplete instance):
// 					- ul: (inputEl) -> HTMLElement (will be added after input el if no parent)
// 					- li: (listItem) -> HTMLElement (will be added into ul if no parent)
// 					- value: (listItem) -> String
// 				- i18n (see below)
// 				- oncomplete
// 				- onopen
// 				- onclose
// 				- onstart
// 				- onstop
// 				- autoselect
//
// 		autocomplete.defaultOptions -> {}

const defaultOptions = {
    i18n: {
        selected: '${value}. Selected ${selected} of ${total}.',
        complete: 'You have selected ${value}',
        found: '${count} suggestions found. Use up or down key to select.',
        notfound: 'No suggestions found.',
    },
    render: {
        wrapper(node) {
            const wrapper = document.createElement('div');
            wrapper.classList.add('Autocomplete--flyout');
            if (node) {
                wrapper.appendChild(node);
            }
            return wrapper;
        },
        flyoutTitle(title) {
            if (title && title !== '') {
                const el = document.createElement('span');
                el.classList.add('Autocomplete--flyoutTitle');
                el.innerText = title;
                return el;
            }
            return undefined;
        },
        ul() {
            const el = document.createElement('ul');
            el.classList.add('Autocomplete--suggestions');
            return el;
        },
        li(data) {
            const el = document.createElement('li');
            el.setAttribute('class', 'will-appear');
            el.setAttribute('tabindex', '-1');
            el.setAttribute('role', 'button');
            el.innerHTML = this.options.render.value.call(this, data);
            return el;
        },
        value(data) {
            return data;
        },
    },
    onselect: null,
    oncomplete: null,
    autoselect: false,
};

function autocomplete(inputEl, listFromValue, defaultsList, prefs = {}) {
    let opened = false;
    let liveEl, listEl, flyoutEl;
    let initialised = false;
    let completed = false;
    let selected = -1,
        selectedItem;
    let savedValue, savedItem, initialValue;
    let sayTimeout;

    const options = deepmerge(defaultOptions, prefs);

    inputEl.addEventListener('focus', handleInputFocus, false);

    const self = {
        destroy,
        close,
        options,
        el: inputEl,
    };
    return self;

    function start() {
        if (initialised) {
            return;
        }

        // create live region to indicate results
        liveEl = document.createElement('div');
        liveEl.setAttribute('role', 'status');
        liveEl.setAttribute('aria-live', 'assertive');
        liveEl.setAttribute('aria-relevant', 'additions');
        // hide for all but screenreader
        liveEl.style.position = 'absolute';
        liveEl.style.pointerEvents = 'none';
        liveEl.style.opacity = '0';
        liveEl.style.left = '-9999px';
        document.body.appendChild(liveEl);

        // create list of suggestions to show
        listEl = options.render.ul.call(self, inputEl);
        flyoutEl = options.render.wrapper.call(self, listEl);

        flyoutEl.style.display = 'none'; // hide by default
        if (!flyoutEl.parentNode) {
            if (inputEl.nextSibling) {
                inputEl.parentNode.insertBefore(flyoutEl, inputEl.nextSibling);
            } else {
                inputEl.parentNode.appendChild(flyoutEl);
            }
        }

        // listen to input events
        inputEl.addEventListener('change', handleInputChange, false);
        inputEl.addEventListener('input', handleInput, false);
        inputEl.addEventListener('keydown', handleKey, false);
        document.addEventListener('focusin', handleDocumentFocus, false);
        document.addEventListener('click', handleDocumentClick, false);
        document.addEventListener('touchstart', handleDocumentTouch, { passive: true });

        initialised = true;

        if (options.onstart) {
            options.onstart();
        }
    }

    function stop() {
        if (!initialised) {
            return;
        }

        inputEl.removeEventListener('change', handleInputChange, false);
        inputEl.removeEventListener('input', handleInput, false);
        inputEl.removeEventListener('keydown', handleKey, false);
        document.removeEventListener('focusin', handleDocumentFocus, false);
        document.removeEventListener('click', handleDocumentClick, false);
        document.removeEventListener('touchstart', handleDocumentTouch, { passive: true });

        liveEl.parentNode.removeChild(liveEl);
        flyoutEl.parentNode.removeChild(flyoutEl);

        initialised = false;

        if (options.onstop) {
            options.onstop();
        }
    }

    function destroy() {
        close();
        stop();
        inputEl.removeEventListener('focus', handleInputFocus, false);
    }

    // interation event handler

    function handleInputFocus(event) {
        start();
        // select all text on focus
        selectAll();

        initialValue = inputEl.value;

        if (completed) {
            return;
        }

        updateSuggestions();
    }

    function handleInputChange(event) {
        if (savedValue !== event.target.value) {
            completed = false;
        }
    }

    function handleInput(event) {
        if (savedValue !== event.target.value) {
            completed = false;
            updateSuggestions();
        }
    }

    function handleDocumentClick(event) {
        if (containsElement(inputEl, event.target)) {
            return;
        }
        let itemEl = event.target;
        while (itemEl && itemEl.parentNode !== listEl) {
            itemEl = itemEl.parentNode;
        }
        if (itemEl) {
            setValueOfItem(itemEl);
            complete();
        } else {
            inputEl.blur();
            stop();
        }
        close();
    }

    // close when touch is not within input or list
    function handleDocumentTouch(event) {
        if (containsElement(inputEl, event.target) || containsElement(listEl, event.target)) {
            return;
        }
        close();
    }

    function handleDocumentFocus(event) {
        if (!containsElement(inputEl, event.target) && !containsElement(flyoutEl, event.target)) {
            close();
            stop();
        }
    }

    function handleKey(event) {
        if (!opened) {
            return;
        }
        switch (event.keyCode) {
            case 40: // down
                event.preventDefault();
                event.stopPropagation();
                selectSuggestion(1);
                break;
            case 38: // up
                event.preventDefault();
                event.stopPropagation();
                selectSuggestion(-1);
                break;
            case 27: // escape
                event.preventDefault();
                event.stopPropagation();
                if (options.autoselect) {
                    setValue(initialValue, true);
                } else {
                    setValue(savedValue, true);
                    selectedItem = savedItem;
                }
                inputEl.focus();
                inputEl.select();
                close();
                break;
            case 13: // enter
                event.preventDefault();
                event.stopPropagation();
                if (options.autoselect) {
                    setValueOfItem(listEl.children[selected]);
                }
                complete();
                break;
            case 9: // tab
                if (options.autoselect) {
                    setValueOfItem(listEl.children[selected]);
                }
                complete();
                break;
            default:
                break;
        }
    }

    function selectSuggestion(value, silent) {
        const inputEls = listEl.children;
        selected = (selected + value + inputEls.length) % inputEls.length;
        let text;
        for (let i = 0; i < inputEls.length; i++) {
            if (i === selected) {
                inputEls[i].classList.add('is-selected');
                text = silent ? options.render.value(inputEls[i]._data) : setValueOfItem(inputEls[i]);
            } else {
                inputEls[i].classList.remove('is-selected');
            }
        }
        if (options.onselect) {
            options.onselect.call(self, inputEls[selected]._data);
        }
        if (!silent) {
            selectAll();
        }
        say(
            fillString(options.i18n.selected, {
                selected: selected + 1,
                total: inputEls.length,
                value: text,
            }),
        );
    }

    function setValueOfItem(itemEl) {
        if (!itemEl) {
            selectedItem = null;
            return;
        }
        const item = itemEl._data;
        const text = options.render.value(item);
        if (text !== false) {
            setValue(text);
        }
        selectedItem = item;
        return text;
    }

    function complete() {
        savedValue = inputEl.value;
        savedItem = selectedItem;
        completed = true;
        say(
            fillString(options.i18n.complete, {
                value: inputEl.value,
            }),
        );
        inputEl.focus();
        selectAll();
        setTimeout(() => {
            close();
        }, 0);

        // trigger change event
        triggerChangeEvents();

        if (options.oncomplete) {
            options.oncomplete.call(self, selectedItem);
        }
    }

    function setValue(value, triggerEvents) {
        if (inputEl.value != value) {
            inputEl.value = value;
            if (triggerEvents) {
                triggerChangeEvents();
            }
        }
    }

    function triggerChangeEvents() {
        let e;
        e = document.createEvent('Event');
        e.initEvent('change', true, false);
        inputEl.dispatchEvent(e);
        e = document.createEvent('Event');
        e.initEvent('input', true, false);
        inputEl.dispatchEvent(e);
    }

    function open() {
        if (!opened) {
            opened = true;
            flyoutEl.style.display = '';
            if (options.onopen) {
                options.onopen();
            }
        }
    }

    function close() {
        if (opened) {
            opened = false;
            selected = -1;
            flyoutEl.style.display = 'none';
            if (options.onclose) {
                options.onclose();
            }
        }
    }

    function say(message, delay) {
        clearTimeout(sayTimeout);
        sayTimeout = setTimeout(() => {
            const { children } = liveEl;
            for (let i = 0; i < children.length; i++) {
                children[i].style.display = 'none';
            }
            liveEl.insertAdjacentHTML('beforeend', `<p>${message}</p>`);
        }, delay || 0);
    }

    function updateSuggestions() {
        const val = inputEl.value;
        savedValue = val;

        const renderTitle = function (title) {
            const titleNode = options.render.flyoutTitle.call(self, title);

            flyoutEl.innerHTML = '';

            if (titleNode) {
                flyoutEl.appendChild(titleNode);
            }

            flyoutEl.appendChild(listEl);
        };

        const renderList = function (list) {
            // render list
            listEl.innerHTML = '';

            list.forEach((data) => {
                const liEl = options.render.li.call(self, data);
                liEl._data = data;

                if (!liEl.parentNode) {
                    listEl.appendChild(liEl);
                }
            });
            if (list.length) {
                open();
                say(
                    fillString(options.i18n.found, {
                        count: list.length,
                        value: val,
                    }),
                    1500,
                );
                if (options.autoselect && val) {
                    selected = 0;
                    selectSuggestion(0, true);
                } else {
                    selected = -1;
                }
            } else if (options.autoselect) {
                open();
                renderTitle(options.i18n.notfound);
            } else {
                close();
                say(
                    fillString(options.i18n.notfound, {
                        count: 0,
                        value: val,
                    }),
                    1500,
                );
            }
        };

        // if inputs is empty and has data-default-values set display them, otherwise show the whole list
        if (val === '' && defaultsList) {
            renderTitle(inputEl.getAttribute('data-default-flyout-title'));

            defaultsList(val, renderList);
        } else {
            renderTitle(inputEl.getAttribute('data-flyout-title'));

            listFromValue(val, renderList);
        }
    }

    function selectAll() {
        const start = 0;
        const end = inputEl.value.length;
        inputEl.select();
        setTimeout(() => {
            inputEl.selectionStart = start;
            inputEl.selectionEnd = end;
            inputEl.setSelectionRange(start, end);
        }, 1);
    }
}

autocomplete.defaultOptions = defaultOptions;

// helpers

function fillString(str, data) {
    if (typeof str === 'function') {
        return str(data);
    }

    for (const key in data) {
        str = str.replace(new RegExp(`\\\${${key}}`, 'g'), data[key]);
    }
    return str;
}

function containsElement(container, element) {
    while (element) {
        if (container === element) {
            return true;
        }
        element = element.parentNode;
    }
    return false;
}

export default autocomplete;
